import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { formatDateTimeEU } from '../utils/dateFormat';

const { FiActivity, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiEye, FiX, FiClock } = FiIcons;

export default function EmailLogs() {
  const { session } = useAuth();
  const { tenantId } = useTenant();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingLog, setViewingLog] = useState(null);

  const loadLogs = async () => {
    if (!tenantId || !session?.token) return;
    try {
      setLoading(true);
      setError('');
      const data = await spformsApi.emailLogsList(session.token, tenantId);
      setLogs(data);
    } catch (err) {
      setError(err.message || 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [tenantId, session?.token]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent': return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-100"> <SafeIcon icon={FiCheckCircle} size={10} /> Sent </span>;
      case 'failed': return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-100"> <SafeIcon icon={FiAlertCircle} size={10} /> Failed </span>;
      default: return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-100"> <SafeIcon icon={FiClock} size={10} /> {status} </span>;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter"> Email Activity </h1>
          <p className="text-gray-500 font-medium tracking-tight"> Track all outgoing communications, delivery status, and errors. </p>
        </div>
        <button onClick={loadLogs} className="w-12 h-12 bg-white border rounded-2xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm" >
          <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700 flex items-center gap-3"> {error} </div>
      ) : null}

      <div className="bg-white border rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b">
              <tr>
                <th className="px-8 py-5">Recipient</th>
                <th className="px-8 py-5">Subject</th>
                <th className="px-8 py-5">Status</th>
                <th className="px-8 py-5 text-right">Sent At</th>
                <th className="px-8 py-5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr> <td colSpan="5" className="p-24 text-center text-gray-400 font-black uppercase tracking-[0.4em] animate-pulse"> Loading logs... </td> </tr>
              ) : logs.length === 0 ? (
                <tr> <td colSpan="5" className="p-24 text-center text-gray-400 font-bold"> No email activity recorded yet. </td> </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-all group">
                    <td className="px-8 py-6">
                      <div className="font-black text-gray-900">{log.recipient_name || '-'}</div>
                      <div className="text-xs font-bold text-gray-400">{log.recipient_email}</div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="text-sm font-bold text-gray-700 line-clamp-1">{log.subject}</div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mt-1">{log.email_type}</div>
                    </td>
                    <td className="px-8 py-6"> {getStatusBadge(log.status)} </td>
                    <td className="px-8 py-6 text-right text-xs font-bold text-gray-500 whitespace-nowrap"> {formatDateTimeEU(log.sent_at || log.created_at)} </td>
                    <td className="px-8 py-6 text-right">
                      <button onClick={() => setViewingLog(log)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 transition-all" > <SafeIcon icon={FiEye} /> </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b flex items-center justify-between bg-gray-50/50">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">Email Content</h2>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Sent to {viewingLog.recipient_email}</p>
              </div>
              <button onClick={() => setViewingLog(null)} className="w-10 h-10 flex items-center justify-center bg-gray-200/50 rounded-full" > <SafeIcon icon={FiX} /> </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-10 space-y-8">
              <div className="space-y-4">
                <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gray-50/50">
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Subject</div>
                  <div className="text-lg font-black text-gray-900">{viewingLog.subject}</div>
                </div>

                <div className="rounded-2xl border-2 border-gray-100 p-8 bg-white">
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 border-b border-gray-100 pb-4">Message Body</div>
                  <div className="text-sm font-medium leading-relaxed text-gray-700 whitespace-pre-wrap">{viewingLog.body}</div>
                </div>

                {viewingLog.error_message && (
                  <div className="rounded-2xl border-2 border-red-100 p-6 bg-red-50">
                    <div className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-2">Error Details</div>
                    <div className="text-sm font-bold text-red-700">{viewingLog.error_message}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-8 border-t bg-gray-50 flex justify-end">
              <button onClick={() => setViewingLog(null)} className="px-10 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-gray-200 hover:bg-black transition-all" > Close Window </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}